import React, { useState } from 'react';
import { Plus, Calendar, ChevronDown, Filter, X, SlidersHorizontal } from 'lucide-react';
interface DashboardToolbarProps {
  dateRange: string;
  onDateRangeChange: (range: string) => void;
  onAddWidget: () => void;
}
export function DashboardToolbar({
  dateRange,
  onDateRangeChange,
  onAddWidget
}: DashboardToolbarProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const dateRanges = [{
    id: '1d',
    label: '1d'
  }, {
    id: '7d',
    label: '7d'
  }, {
    id: '1m',
    label: '1m'
  }, {
    id: '3m',
    label: '3m'
  }, {
    id: '1y',
    label: '1y'
  }];
  const DateRangePills = () => <div className="flex items-center border border-gray-300 rounded-md overflow-hidden bg-white" data-id="element-51">
      {dateRanges.map(range => <button key={range.id} onClick={() => onDateRangeChange(range.id)} className={`px-3 py-1.5 text-xs font-medium border-r border-gray-200 last:border-r-0 transition-colors ${dateRange === range.id ? 'bg-blue-50 text-blue-700 border-blue-200 relative z-10' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-52">
          {range.label}
        </button>)}
      <button onClick={() => onDateRangeChange('custom')} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${dateRange === 'custom' ? 'bg-blue-50 text-blue-700 border-l border-blue-200 relative z-10' : 'text-gray-600 hover:bg-gray-50 border-l border-gray-200'}`} data-id="element-53">
        <Calendar className="w-3.5 h-3.5" data-id="element-54" />
        <ChevronDown className="w-3.5 h-3.5" data-id="element-55" />
      </button>
    </div>;
  return <div className="mb-6" data-id="element-56">
      {/* Mobile compact toolbar */}
      <div className="sm:hidden bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden" data-id="element-57">
        <div className="flex items-center justify-between gap-2 p-2" data-id="element-58">
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0" data-id="element-59">
            {dateRanges.map(range => <button key={range.id} onClick={() => onDateRangeChange(range.id)} className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${dateRange === range.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-id="element-60">
                {range.label}
              </button>)}
            <button onClick={() => onDateRangeChange('custom')} className={`flex items-center px-2 py-1 transition-colors ${dateRange === 'custom' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`} data-id="element-61">
              <Calendar className="w-3 h-3" data-id="element-62" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-63">
            <button onClick={() => setFiltersExpanded(!filtersExpanded)} className={`relative flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${filtersExpanded ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-64">
              <SlidersHorizontal className="w-3.5 h-3.5" data-id="element-65" />
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-accent text-white text-[8px] font-bold rounded-full flex items-center justify-center" style={{
              backgroundColor: 'var(--accent-color)'
            }} data-id="element-66">
                1
              </span>
            </button>
            <button onClick={onAddWidget} className="p-1.5 rounded-lg text-white transition-opacity hover:opacity-90" style={{
            backgroundColor: 'var(--accent-color)'
          }} aria-label="Add Widget" data-id="element-67">
              <Plus className="w-3.5 h-3.5" data-id="element-68" />
            </button>
          </div>
        </div>

        <div className={`overflow-hidden transition-all duration-200 ease-in-out ${filtersExpanded ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-69">
          <div className="px-2 pb-2 pt-0.5 flex items-center gap-2 border-t border-gray-100" data-id="element-70">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-[11px] font-medium text-gray-700 whitespace-nowrap" data-id="element-71">
              <Filter className="w-2.5 h-2.5 text-gray-400" data-id="element-72" />
              <span data-id="element-73">
                Location is <span className="font-semibold" data-id="element-74">Tiruppur</span>
              </span>
              <button className="ml-0.5 text-gray-400 hover:text-gray-600" data-id="element-75">
                <X className="w-2.5 h-2.5" data-id="element-76" />
              </button>
            </div>
            <button className="flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap" data-id="element-77">
              <Plus className="w-2.5 h-2.5" data-id="element-78" />
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Tablet toolbar (sm to lg) — two-row layout */}
      <div className="hidden sm:flex lg:hidden flex-col gap-2.5 bg-white p-3 rounded-xl border border-gray-200 soft-shadow" data-id="element-79">
        {/* Row 1: Add Widget + Filters */}
        <div className="flex items-center gap-3" data-id="element-80">
          <button onClick={onAddWidget} className="flex items-center gap-1.5 bg-accent text-white px-3.5 py-1.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0" style={{
          backgroundColor: 'var(--accent-color)'
        }} data-id="element-81">
            <Plus className="w-4 h-4" data-id="element-82" />
            Add Widget
          </button>

          <div className="h-5 w-px bg-gray-200 flex-shrink-0" data-id="element-83"></div>

          <div className="flex items-center gap-2" data-id="element-84">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700 whitespace-nowrap" data-id="element-85">
              <Filter className="w-3 h-3 text-gray-500" data-id="element-86" />
              <span data-id="element-87">
                Location is <span className="font-semibold" data-id="element-88">Tiruppur</span>
              </span>
              <button className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-89">
                <X className="w-3 h-3" data-id="element-90" />
              </button>
            </div>

            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap" data-id="element-91">
              <Filter className="w-3 h-3" data-id="element-92" />
              Add Filter
            </button>
          </div>
        </div>

        {/* Row 2: Date range pills */}
        <div className="flex items-center" data-id="element-93">
          <DateRangePills data-id="element-94" />
        </div>
      </div>

      {/* Desktop toolbar (lg+) — single-row layout */}
      <div className="hidden lg:flex flex-row items-center justify-between gap-4 bg-white p-3 rounded-xl border border-gray-200 soft-shadow" data-id="element-95">
        <div className="flex items-center gap-3" data-id="element-96">
          <button onClick={onAddWidget} className="flex items-center gap-1.5 bg-accent text-white px-3.5 py-1.5 rounded-md text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0" style={{
          backgroundColor: 'var(--accent-color)'
        }} data-id="element-97">
            <Plus className="w-4 h-4" data-id="element-98" />
            Add Widget
          </button>

          <div className="h-5 w-px bg-gray-200 mx-1 flex-shrink-0" data-id="element-99"></div>

          <div className="flex items-center gap-2" data-id="element-100">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-xs font-medium text-gray-700 whitespace-nowrap" data-id="element-101">
              <Filter className="w-3 h-3 text-gray-500" data-id="element-102" />
              <span data-id="element-103">
                Location is <span className="font-semibold" data-id="element-104">Tiruppur</span>
              </span>
              <button className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-105">
                <X className="w-3 h-3" data-id="element-106" />
              </button>
            </div>

            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap" data-id="element-107">
              <Filter className="w-3 h-3" data-id="element-108" />
              Add Filter
            </button>
          </div>
        </div>

        <div className="flex items-center flex-shrink-0" data-id="element-109">
          <DateRangePills data-id="element-110" />
        </div>
      </div>
    </div>;
}